<template>
  <FormKit name="comPreferences" type="form" @submit="onSave">
    <div class="">
      <FormStepInputs
        :step="{
          stepName: name,
          formInputs: content,
        }"
      >
        <template #switchToDigitalUpdatesMessage>
          <div class="mb-8 max-w-[704px]">
            <InformationBox
              :title="switchToDigitalUpdatesMessage.title.value"
              class="flex items-start gap-4 p-5 px-6 self-stretch"
              :description="switchToDigitalUpdatesMessage.content.value"
              :type="switchToDigitalUpdatesMessage.messageType.value"
              :isCircle="true"
              :useButtonSlot="false"
            ></InformationBox>
          </div>
        </template>
        <template #transactionalCommsTitle>
          <div class="mt-8">
            <PanelTitle v-bind="getFormInput(props.content, 'transactionalCommsTitle')"></PanelTitle>
          </div>
        </template>
        <template #transactionalOptionInvalidMessage>
          <div v-if="model.deliveryChannelNoticesInvalid" class="max-w-[704px] mt-5">
            <InformationBox
              :title="transactionalOptionInvalidMessage.title.value"
              class="flex items-start gap-4 p-5 px-6 self-stretch"
              :description="transactionalOptionInvalidMessage.content.value"
              :type="transactionalOptionInvalidMessage.messageType.value"
              :isCircle="true"
              :useButtonSlot="false"
              :hideIcon="true"
            ></InformationBox>
          </div>
        </template>
        <template #transactionalOptionsLabel>
          <div class="mt-6">
            <FormKit
              v-model="model.deliveryChannelNotices"
              :label="transactionalOptionsLabel?.label?.value"
              :innerClass="'text-base'"
              inputClass="text-lg"
              type="nativeRadio"
              name="deliveryChannelNotices"
              outerClass="-mt-1"
              :options="getOptionsForInput()"
              @input="(e) => onOptionChange(e)"
            ></FormKit>
          </div>
        </template>
        <template #agmNoticesCommsTitle>
          <div class="mt-4">
            <PanelTitle v-bind="getFormInput(props.content, 'agmNoticesCommsTitle')"></PanelTitle>
          </div>
        </template>
        <template #agmOptionInvalidMessage>
          <div v-if="model.deliveryChannelAgmInvalid" class="max-w-[704px] mt-5">
            <InformationBox
              :title="agmOptionInvalidMessage.title.value"
              class="flex items-start gap-4 p-5 px-6 self-stretch"
              :description="agmOptionInvalidMessage.content.value"
              :type="agmOptionInvalidMessage.messageType.value"
              :isCircle="true"
              :useButtonSlot="false"
              :hideIcon="true"
            ></InformationBox>
          </div>
        </template>
        <template #agmOptionsLabel>
          <div class="mt-6">
            <FormKit
              v-model="model.deliveryChannelAgm"
              :label="agmOptionsLabel?.label?.value"
              :innerClass="'text-base'"
              inputClass="text-lg"
              type="nativeRadio"
              name="deliveryChannelAGM"
              outerClass="-mt-1"
              :options="getOptionsForInput()"
              @input="(e) => onOptionChange(e)"
            ></FormKit>
          </div>
        </template>
      </FormStepInputs>
      <div class="flex w-[640px] mt-12 items-start content-start gap-4 flex-wrap">
        <Link :field="discardChangesLink?.link" class="btn btn-tertiary btn-md">
          <div class="flex items-center space-x-2">
            <img src="@/assets/icons/arrow-left.svg" alt="left arrow" class="w-5 h-5 inline-block" />
            <span>{{ discardChangesLink?.link?.value?.text }}</span>
          </div>
        </Link>
        <button type="submit" :disabled="!hasChanges" class="btn btn-md btn-primary">Save changes</button>
      </div>
      <div class="!mt-10 mb-10 md:!mt-12 md:!mb-12">
        <Divider></Divider>
      </div>
      <FullScreenSpinner v-if="showSavingOrLoading" :applyOpacity="true"></FullScreenSpinner>
    </div>
  </FormKit>
</template>
<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { useAccountStore, useAnalyticsStore } from '@/store';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import {
  CommunicationPreferences,
  CommunicationPreferenceOptions,
  getEnumValue,
} from '@/interfaces/self-service/communication-preferences';
import { SelfServiceService } from '@/services/store';
import Divider from '@/components/Divider/Divider.vue';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { useI18n } from 'vue-i18n';
import { FormEvents } from '@/enums/form-events.enum';
import { Link } from '@sitecore-jss/sitecore-jss-vue';

const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();
const analyticsStore = useAnalyticsStore();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const communicationPreferenceOptions = [
  { label: 'Postal mail', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.PostalPhysical] },
  { label: 'Email or SMS', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.DigitalEmail] },
];

const showSavingOrLoading = ref<boolean>(false);
const switchToDigitalUpdatesMessage = ref(getFormInput(props.content, 'switchToDigitalUpdatesMessage'));
const transactionalOptionsLabel = ref(getFormInput(props.content, 'transactionalOptionsLabel'));
const agmOptionsLabel = ref(getFormInput(props.content, 'agmOptionsLabel'));
const transactionalOptionInvalidMessage = ref(getFormInput(props.content, 'transactionalOptionInvalidMessage'));
const agmOptionInvalidMessage = ref(getFormInput(props.content, 'agmOptionInvalidMessage'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const subscriptions = <any>[];
const hasChanges = ref<boolean>(false);

const model = ref<any>({
  deliveryChannelNotices: '',
  deliveryChannelAgm: '',
  deliveryChannelNoticesInvalid: false,
  deliveryChannelAgmInvalid: false,
});

const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

onMounted(async () => {
  await loadPreferences();
  pushAnalytics();
});

const loadPreferences = async () => {
  showSavingOrLoading.value = true;
  try {
    const d365Id = accountStore.identityDetails?.d365Id ?? '';
    if (d365Id) {
      subscriptions.values = await SelfServiceService.getSubscriptions(d365Id);

      model.value.deliveryChannelNotices = getSelectedValueByValue(subscriptions.values.deliveryChannelNotices);
      model.value.deliveryChannelAgm = getSelectedValueByValue(subscriptions.values.deliveryChannelAgm);

      model.value.deliveryChannelNoticesInvalid = !model.value.deliveryChannelNotices;
      model.value.deliveryChannelAgmInvalid = !model.value.deliveryChannelAgm;
    }
  } catch (error) {
    console.error(error);
  }

  await nextTick();
  showSavingOrLoading.value = false;
};

const onOptionChange = (e: any) => {
  if (showSavingOrLoading.value) return;
  hasChanges.value = true;
};

const getOptionsForInput = () => {
  return communicationPreferenceOptions.map((opt) => {
    return opt.label;
  });
};

const getSelectedValueByValue = (value: string) => {
  const enumValue = getEnumValue(value);
  if (enumValue !== undefined) {
    const selected = communicationPreferenceOptions.find((x) => x.value === CommunicationPreferenceOptions[enumValue]);
    return selected?.label;
  }
  return '';
};

const getSelectedValueByDisplayValue = (selectedOption: string) => {
  const selected = communicationPreferenceOptions.find((x) => x.label.toLowerCase() === selectedOption?.toLowerCase());
  return selected?.value;
};

const isInvalid = (submitModel: CommunicationPreferences) => {
  model.value.deliveryChannelNoticesInvalid = !submitModel.deliveryChannelNotices;
  model.value.deliveryChannelAgmInvalid = !submitModel.deliveryChannelAgm;
  return model.value.deliveryChannelNoticesInvalid || model.value.deliveryChannelAgmInvalid;
};

const onSave = async () => {
  try {
    const submitModel = {
      deliveryChannelNotices: getSelectedValueByDisplayValue(model.value.deliveryChannelNotices),
      deliveryChannelAgm: getSelectedValueByDisplayValue(model.value.deliveryChannelAgm),
    } as CommunicationPreferences;

    if (isInvalid(submitModel)) {
      return;
    }

    showSavingOrLoading.value = true;

    const d365Id = accountStore.identityDetails?.d365Id ?? '';

    if (d365Id) {
      const response = await SelfServiceService.communicationsPreferences(d365Id, submitModel);

      if (response) {
        hasChanges.value = false;

        toastAlertStore.addToastAlert({
          title: 'Communication preferences updated',
          description: '',
          type: ToastAlertType.SUCCESS,
          duration: 6000,
        });

        showSavingOrLoading.value = false;

        await analyticsStore.pushAnalytics({
          event: FormEvents.COMPLETE,
          form: {
            name: 'Communication preferences',
            stepName: 'FormCompleted',
          },
          profileInfo: {
            agmCommPref: model.value.deliveryChannelAgm,
            transCommPref: model.value.deliveryChannelNotices,
          },
        });

        return;
      }
    }
  } catch (error) {
    showSavingOrLoading.value = false;
    toastAlertStore.addToastAlert({
      title: 'Unable to save changes',
      description: '',
      type: ToastAlertType.ERROR,
      duration: 6000,
    });

    await analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Communication preferences',
        formErrorType: 'Unable to save changes',
        formErrorDetail: 'Unable to save changes',
      },
    });
  }
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);

  pageAnalytics.pageAnalytics.profileInfo!.agmCommPref = model.value.deliveryChannelAgm;
  pageAnalytics.pageAnalytics.profileInfo!.transCommPref = model.value.deliveryChannelNotices;

  pageAnalytics.pushAnalytics();
};
</script>
